// Sending photos from a phone (spec 5.4). The photo goes as the camera took it,
// in whatever format the phone saves (HEIC on iPhones): our server turns it the
// right way up, strips where and when it was taken, and shrinks it. Only a photo
// over the size limit is shrunk here first. A dropped signal is tried again on
// its own, and the photo stays on this page so "Try again" never means taking it again.
import { isPhotoType, MAX_PHOTO_BYTES, photoTypeOf } from "@crm/schemas/photos";
import { api } from "./api.ts";
import { PhotoRefused } from "./bytes.ts";

const MAX_EDGE = 2560;

/** Shrinks a photo only when it's too big to send (the browser can't read HEIC everywhere: then it goes as it is). */
export async function prepareImage(file: File): Promise<File> {
	if (!file.type.startsWith("image/") || file.size <= MAX_PHOTO_BYTES) return file;
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
	} catch {
		return file;
	}
	const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
	const canvas = new OffscreenCanvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale));
	canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
	bitmap.close();
	const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.9 });
	return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
}

/** A storage server refusal (too big, wrong type): trying again won't help. */
class Refused extends Error {}

function postOnce(
	upload: { url: string; fields: Record<string, string> },
	file: File,
	onProgress: (sent: number, total: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const form = new FormData();
		for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
		form.append("file", file);
		const xhr = new XMLHttpRequest();
		xhr.open("POST", upload.url);
		xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded, e.total);
		xhr.onload = () =>
			xhr.status < 300
				? resolve()
				: xhr.status < 500
					? reject(new Refused("That file is too large or not an accepted type."))
					: reject(new Error(`Upload failed (${xhr.status})`));
		xhr.onerror = () => reject(new Error("Upload failed. Check your connection and try again."));
		xhr.send(form);
	});
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Uploads to a signed storage form with progress (fetch has no upload progress).
 * The storage server enforces the size cap and content type in the form. A
 * dropped connection is tried twice more, after 1 and then 4 seconds.
 */
export async function uploadWithProgress(
	upload: { url: string; fields: Record<string, string> },
	file: File,
	onProgress: (fraction: number, sent: number, total: number) => void,
): Promise<void> {
	for (let attempt = 1; ; attempt++) {
		try {
			return await postOnce(upload, file, (sent, total) => onProgress(sent / total, sent, total));
		} catch (e) {
			if (e instanceof Refused || attempt === 3) throw e;
			onProgress(0, 0, file.size);
			await wait(attempt === 1 ? 1000 : 4000);
		}
	}
}

/** What the phone says the photo hashes to, so the same one isn't kept twice. Optional: the server works out the real one. */
async function hashOf(file: File): Promise<string | undefined> {
	try {
		const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
		return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
	} catch {
		return undefined;
	}
}

/**
 * Send a candidate's photo from a link page: ask for an upload form at `base`/files,
 * post the photo, then confirm. Returns the file id.
 */
export async function sendCandidatePhoto(
	base: string,
	file: File,
	onProgress: (sent: number, total: number) => void,
	extra: Record<string, unknown> = {},
): Promise<string> {
	const prepared = await prepareImage(file);
	const type = photoTypeOf(prepared);
	if (!isPhotoType(type) && (type !== "application/pdf" || extra.selfie))
		throw new PhotoRefused(
			extra.selfie
				? "That isn't a photo. Take a selfie with your camera."
				: "That isn't a photo or a PDF. Take a photo with your camera.",
		);
	if (prepared.size > MAX_PHOTO_BYTES) throw new PhotoRefused("That file is too big. Take a photo instead.");
	const sha256 = await hashOf(prepared);
	const created = await api<{ id: string; upload: { url: string; fields: Record<string, string> } }>(`${base}/files`, {
		body: {
			name: prepared.name,
			contentType: type,
			size: prepared.size,
			...(sha256 ? { sha256 } : {}),
			...extra,
		},
	});
	try {
		await uploadWithProgress(created.upload, prepared, (_, sent, total) => onProgress(sent, total));
	} catch (e) {
		if (e instanceof Refused)
			throw new PhotoRefused("That file is too big or isn't a photo. Take a photo with your camera.");
		throw e;
	}
	const { selfie: _selfie, ...confirm } = extra;
	await api(`${base}/files/${created.id}/complete`, { body: confirm });
	return created.id;
}

export { formatBytes } from "./bytes.ts";
